import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { applyDatesToScripts } from "./data/page-dates";

type HeadResult = { scripts?: Array<{ type?: string; children?: string }> } & Record<string, unknown>;
type HeadFn = (ctx: { match: { pathname: string } }) => HeadResult | Promise<HeadResult> | undefined;

// Stamp JSON-LD dateModified/datePublished from src/data/page-dates.ts on every route head.
function withPageDates(head: HeadFn): HeadFn {
  const apply = (path: string, r: HeadResult | undefined) =>
    r && r.scripts ? { ...r, scripts: applyDatesToScripts(path, r.scripts) } : r;
  return (ctx) => {
    const r = head(ctx);
    return r instanceof Promise ? r.then((v) => apply(ctx.match.pathname, v) as HeadResult) : (apply(ctx.match.pathname, r) as HeadResult);
  };
}

export const getRouter = () => {
  const queryClient = new QueryClient();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
  });

  for (const route of Object.values(router.routesById) as Array<{ options: { head?: unknown } }>) {
    const head = route.options.head as HeadFn | undefined;
    if (head && !(head as { __pageDates?: boolean }).__pageDates) {
      const wrapped = withPageDates(head);
      (wrapped as { __pageDates?: boolean }).__pageDates = true;
      route.options.head = wrapped;
    }
  }

  return router;
};
